import { forwardRef, useImperativeHandle, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { MyProfileFormSchema } from '@/components/forms/schemas/MyProfileFormSchema';
import { FormCreatableMultiSelect } from '@/components/global/form-inputs/FormCreatableMultiSelect';
import { FormImage } from '@/components/global/form-inputs/FormImage';
import { FormInput } from '@/components/global/form-inputs/FormInput';
import { FormMultiSelect } from '@/components/global/form-inputs/FormMultiSelect';
import { Form } from '@/components/ui/form';
import { CONSULTANT_FIRMS } from '@/constants/consultant-firms';
import { US_STATES } from '@/constants/territories/states';

interface MyProfileFormProps {
  form: UseFormReturn<z.infer<typeof MyProfileFormSchema>>;
}

export const MyProfileForm = forwardRef(({ form }: MyProfileFormProps, ref) => {
  // state
  const [customFirms, setCustomFirms] = useState<Record<string, string>>({});

  const stateOptions = Object.entries(US_STATES).map(([key, value]) => ({
    value: key,
    label: value,
  }));

  const allConsultantFirms = {
    ...CONSULTANT_FIRMS,
    ...customFirms,
  };

  const handleCreateFirm = (firmName: string) => {
    const key = firmName.replace(/\s+/g, '');
    setCustomFirms((prev) => ({
      ...prev,
      [key]: firmName,
    }));

    // Auto-select the newly created firm
    const currentValues = form.getValues('consultant_firm_affiliated') || [];
    form.setValue('consultant_firm_affiliated', [...currentValues, key]);
  };

  // imperative handle
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      form.handleSubmit(async () => {})();
    },
  }));

  return (
    <Form {...form}>
      <form className="w-full space-y-1.5">
        <div className="flex items-center gap-6 rounded-xl border border-border bg-white p-6 max-md:flex-col">
          <div className="as-title-03 as-strong text-zinc-700 max-md:order-2 max-md:text-center">
            Profile photo
            <p className="as-title-03 font-normal">
              This will be displayed in the application
            </p>
          </div>
          <div className="max-md:order-1 lg:min-w-[280px]">
            <FormImage
              name="photo_url"
              control={form.control}
              setValue={form.setValue}
            />
          </div>
        </div>
        <div className="flex flex-col gap-6 rounded-xl border border-border bg-white p-6">
          <p className="as-title-02 as-strong w-[280px] font-medium text-zinc-700">
            Contact Details
          </p>
          <div className="grid flex-1 grid-cols-2 gap-6 max-sm:grid-cols-1">
            <FormInput
              name="first_name"
              label="First Name"
              control={form.control}
            />
            <FormInput
              name="last_name"
              label="Last Name"
              control={form.control}
            />
            <FormCreatableMultiSelect
              name="consultant_firm_affiliated"
              label="Consultant Firm Affiliated"
              control={form.control}
              options={Object.entries(allConsultantFirms).map(
                ([key, value]) => ({
                  value: key,
                  label: value,
                })
              )}
              onCreateOption={handleCreateFirm}
              placeholder="Select or type to add a new firm"
              searchPlaceholder="Search firms or type to add new..."
            />
            <FormInput name="city" label="City" control={form.control} />
            <FormInput name="state" label="State" control={form.control} />
            <FormInput
              name="zip_code"
              label="Zip Code"
              control={form.control}
            />
            <FormInput name="address" label="Address" control={form.control} />
            <FormInput
              type="phone"
              name="phone"
              label="Phone"
              control={form.control}
            />
            <FormInput name="email" label="Email" control={form.control} />
            <FormInput
              name="meeting_link"
              label="Meeting Link"
              control={form.control}
            />
            <FormInput
              name="linkedin"
              label="LinkedIn"
              control={form.control}
            />
            <FormInput name="website" label="Website" control={form.control} />
          </div>
        </div>
        <div className="flex items-center gap-6 rounded-xl border border-border bg-white p-6 max-md:flex-col max-md:items-start">
          <div className="as-title-03 as-strong min-w-[280px] text-zinc-700">
            Preferred Market
            <p className="as-title-03 font-normal">You can select 20 max.</p>
          </div>
          <FormMultiSelect
            name="preferred_markets"
            control={form.control}
            options={stateOptions}
            className="w-full"
            maxSelections={20}
            placeholder="Select up to 20 markets"
          />
        </div>
      </form>
    </Form>
  );
});
